import joi, { ObjectSchema } from 'joi';
import moment from 'moment';
import { IResearchProposal, IResearchProposalStudyDesign } from '@vivli/features/data-requests/infrastructure/interface';
import { JoiValidationOptionsConstant } from '@vivli/shared/infrastructure/constants';

// Months from request creation to the earliest Target Analysis Start Date, and from that start date to the
// earliest Estimated Analysis Completion Date. The backend keeps the same two numbers on Timeline
// (DefaultProjectStartMonths / DefaultAnalysisCompletionMonths) and stamps its defaults with .NET AddMonths,
// which clamps to the last day of the month when the day does not exist: September 30 + 5 months is
// February 28. moment's add(n, 'months') clamps the same way. Never build these with new Date(y, m + n, d)
// or setFullYear: JavaScript overflows a missing day into the next month instead, which is how the default
// came to fail its own validation on month-end creation dates (DEV-1106).
//
// The minimums are anchored at UTC midnight of the UTC calendar day of the anchor date, not at local
// midnight of the browser's local day. The backend stamps its defaults in UTC, and the two calendars differ
// for a few hours around UTC midnight; when the two days clamp to the same target (September 29 and 30 both
// give February 28), a local-day minimum lands a few hours after the backend default and rejects it. UTC
// midnight of the UTC day is on or before any instant the backend can produce for that day, in every zone.
const MONTHS_FROM_CREATION_TO_START = 5;
const MONTHS_FROM_START_TO_COMPLETION = 12;

const earliestDateMonthsAfter = (anchor: Date, months: number): Date => moment.utc(anchor).startOf('day').add(months, 'months').toDate();

const studyDesignSchema: ObjectSchema<IResearchProposalStudyDesign> = joi
    .object({
        briefDescription: joi.string().max(9000).required().label('Brief Description'),
        elementsforOutcomes: joi.string().max(9000).required().label('Outcomes Elements Description'),
        mainPredictorIndependentVariable: joi.string().max(5000).required().label('Main Predictor/Independent Variable Description'),
        otherVariablesOfInterest: joi.string().max(9000).label('Other Variables Description').allow('').optional()
    })
    .options(JoiValidationOptionsConstant);

const getTimelineSchema = (isAdmin: boolean, researchProposal: IResearchProposal, earliestStartDate?: Date) => {
    //no min date validation for vivli admin
    if (isAdmin) {
        return joi.object({
            projectStartDate: joi.date().required().label('Project Start Date at least 5 Months After Request Start Date'),
            analysisCompletionDate: joi.date().required().label('Analysis Completion Date')
        });
    } else {
        return joi.object({
            projectStartDate: joi
                .date()
                .required()
                .label('Project Start Date at least 5 Months After Request Start Date')
                .min(earliestStartDate)
                .message(`Project Start Date must be at least ${MONTHS_FROM_CREATION_TO_START} months after the Request Start Date.`)
                .messages({
                    'date.min': `Project Start Date must be at least ${MONTHS_FROM_CREATION_TO_START} months after the Request Start Date.`
                }),
            analysisCompletionDate: joi
                .date()
                .required()
                .label('Analysis Completion Date')
                .custom((value, helpers) => {
                    // Access the value of the 'projectStartDate' field using helpers.state.ancestors[0]
                    const startDate = helpers.state.ancestors[0].projectStartDate;

                    // If start_date is not defined yet, skip validation.
                    if (!startDate) {
                        return value;
                    }

                    // 12 calendar months after the start date, clamped to month end like the backend default
                    const twelveMonthsLater = earliestDateMonthsAfter(startDate, MONTHS_FROM_START_TO_COMPLETION);

                    // Compare the end_date with the calculated date
                    if (value.getTime() < twelveMonthsLater.getTime()) {
                        return helpers.error('date.greaterThanTwelveMonths', {
                            start_date: startDate.toISOString().split('T')[0]
                        });
                    }

                    return value;
                }, `Date must be at least ${MONTHS_FROM_START_TO_COMPLETION} months from start date`)
        });
    }
};

export const getResearchProposalSchema = (isAdmin: boolean, researchProposal: IResearchProposal, requestStartDate: Date) => {
    const parsedDate = requestStartDate ? new Date(requestStartDate) : null;
    const earliestStartDate = parsedDate ? earliestDateMonthsAfter(parsedDate, MONTHS_FROM_CREATION_TO_START) : new Date();

    return joi
        .object({
            laySummary: joi.string().required().label('Lay Summary'),
            specificAimsObjectives: joi.string().required().label('Objective'),
            purposeOfAnalysis: joi.array().min(1).required().label('Purpose of Analysis').default([]),
            outcomes: joi.array().min(1).required().label('Outcome(s)').default([]),
            researchProposalStudyDesign: studyDesignSchema,
            projectTimeline: getTimelineSchema(isAdmin, researchProposal, earliestStartDate),
            disseminationAndPublicationPlan: joi.string().max(9000).label('Publication Plan'),
            purposeOfAnalysisOtherDescription: joi.when('purposeOfAnalysis', {
                is: joi.array().items(joi.string().valid('Other').required(), joi.any()).required(),
                then: joi.string().required().label('Other Purpose of Analysis'),
                otherwise: joi.optional()
            }),
            outcomesOtherDescription: joi.when('outcomes', {
                is: joi.array().items(joi.string().valid('Other').required(), joi.any()).required(),
                then: joi.string().required().label('Other Outcome'),
                otherwise: joi.optional()
            })
        })
        .options(JoiValidationOptionsConstant);
};
